import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Spin, message } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import { useIntl } from 'react-intl';
import { fetchSetting, uchatApi } from '../services/api';
import type { Category, CategoryConfig, CategoryQuestionNode } from '../types/category';
import { hasQuestionTree, isQuestionBranch } from '../types/categoryQuestionTree';
import { PageThemeProvider } from '../components/uchat/v2/theme/PageThemeProvider';
import { resolveV2Theme } from '../components/uchat/v2/theme/registry';
import { toChatThemeColors } from '../components/uchat/v2/theme/toChatThemeColors';
import { usePageTheme } from '../components/uchat/v2/theme/usePageTheme';
import UCCategoryPage from '../components/uchat/v2/UCCategoryPage';
import UCQuestionPage from '../components/uchat/v2/UCQuestionPage';
import UCEndingPage from '../components/uchat/v2/UCEndingPage';
import UCChatView from '../components/uchat/v2/UCChatView';

type ViewMode = 'category' | 'question' | 'chat' | 'ending';

interface SessionParams {
  csUuid: string;
  code: string;
  category?: string;
  status?: string;
}

const parseCategoryData = (rawCategory: unknown): Category | undefined => {
  if (!rawCategory) return undefined;
  if (typeof rawCategory === 'string') {
    try {
      return JSON.parse(rawCategory) as Category;
    } catch {
      return undefined;
    }
  }
  return rawCategory as Category;
};

const parseHideHeader = (search: string): boolean =>
  new URLSearchParams(search).get('hideHeader')?.toLowerCase() === 'true';

const UChatPageV2Inner: React.FC = () => {
  const intl = useIntl();
  const location = useLocation();
  const { tokens } = usePageTheme();
  const chatThemeColors = useMemo(() => toChatThemeColors(tokens), [tokens]);
  const isDark = tokens.appearance === 'dark';

  const [code, setCode] = useState('');
  const [uid, setUid] = useState('');
  const [username, setUsername] = useState('');
  const [categoryData, setCategoryData] = useState<Category | undefined>();
  const [categoryLoaded, setCategoryLoaded] = useState(false);
  const [selectedCategoryConfig, setSelectedCategoryConfig] = useState<CategoryConfig | undefined>();
  const [questionStack, setQuestionStack] = useState<CategoryQuestionNode[][]>([]);
  const [params, setParams] = useState<SessionParams | undefined>();
  const [viewMode, setViewMode] = useState<ViewMode>('category');
  const [loading, setLoading] = useState(false);
  const [hasInitialized, setHasInitialized] = useState(false);
  const [switchChatEvaluate, setSwitchChatEvaluate] = useState(false);
  const hideHeader = useMemo(() => parseHideHeader(location.search), [location.search]);

  const chatRef = useRef<{ fetchChatContent: () => Promise<void> }>();
  const handleChatRef = useCallback((ref: { fetchChatContent: () => Promise<void> }) => {
    chatRef.current = ref;
  }, []);

  const buildChatUrl = useCallback(
    (p: { csUuid?: string; code: string; uid: string; username: string; category?: string }) => {
      const searchParams = new URLSearchParams(window.location.search);
      if (p.csUuid) {
        searchParams.set('csUuid', p.csUuid);
      } else {
        searchParams.delete('csUuid');
      }
      searchParams.set('code', p.code);
      searchParams.set('uid', p.uid);
      searchParams.set('username', p.username);
      if (p.category) {
        searchParams.set('category', p.category);
      } else {
        searchParams.delete('category');
      }
      searchParams.set('hideHeader', hideHeader ? 'true' : 'false');
      return `${window.location.pathname}?${searchParams.toString()}`;
    },
    [hideHeader]
  );

  const applySetting = useCallback((settingData: any, robotCode: string) => {
    if (settingData?.category) {
      const parsed = parseCategoryData(settingData.category);
      if (parsed) setCategoryData(parsed);
    }
    setSwitchChatEvaluate(!!settingData?.switchChatEvaluate);
    setCategoryLoaded(true);
    if (robotCode) setCode(robotCode);
  }, []);

  /** 进入会话并同步地址栏（uid/csUuid），避免刷新后重新生成游客会话 */
  const enterChat = useCallback(
    (
      session: {
        csUuid: string;
        code: string;
        category?: string;
        status?: string;
      },
      identity: { uid: string; username: string }
    ) => {
      setParams(session);
      if (session.status === 'close' && switchChatEvaluate) {
        setViewMode('ending');
      } else {
        setViewMode('chat');
      }
      if (session.csUuid && session.code && identity.uid) {
        window.history.replaceState(
          {},
          '',
          buildChatUrl({
            csUuid: session.csUuid,
            code: session.code,
            uid: identity.uid,
            username: identity.username || identity.uid,
            category: session.category
          })
        );
      }
    },
    [switchChatEvaluate, buildChatUrl]
  );

  const handleCategorySelect = useCallback(
    async (config: CategoryConfig) => {
      setSelectedCategoryConfig(config);
      setLoading(true);
      try {
        try {
          const chattingResp = await uchatApi.chattingList(code, uid);
          const list = Array.isArray(chattingResp?.data) ? chattingResp.data : [];
          const existing = list.find((cs: any) => cs.category === config.name);
          // 该分类已有未关闭会话：直进原聊天，不再选题
          if (existing?.uuid) {
            enterChat(
              {
                csUuid: existing.uuid,
                code: existing.kbUuid || existing.code || code,
                category: config.name,
                status: existing.status
              },
              { uid, username }
            );
            return;
          }
        } catch (error) {
          console.error(error);
        }
        // 已配置问题树：先选题，不建会话
        if (hasQuestionTree(config)) {
          setQuestionStack([config.questionTree || []]);
          setViewMode('question');
          return;
        }
        const searchParams = new URLSearchParams(window.location.search);
        const urlToken = searchParams.get('token');
        const createData: Record<string, string> = {
          code,
          uid,
          username,
          category: config.name
        };
        if (urlToken) createData.token = urlToken;

        const createResp = await uchatApi.createCs(createData);
        if (createResp?.success === false && createResp?.code === 'C_1') {
          return;
        }
        if (createResp?.success && createResp.data) {
          const data = createResp.data;
          const newCode = data.code || code;
          if (newCode !== code) setCode(newCode);
          enterChat(
            {
              csUuid: data.uuid,
              code: newCode,
              category: config.name,
              status: data.status
            },
            { uid, username }
          );
        } else {
          message.error(createResp?.msg || 'Create session failed');
        }
      } catch (error) {
        console.error(error);
        message.error(intl.formatMessage({ id: 'chat.init_failed' }));
      } finally {
        setLoading(false);
      }
    },
    [code, uid, username, enterChat, intl]
  );

  const enterChatFromCategory = useCallback(
    async (config: CategoryConfig, questionLeafId?: string) => {
      setLoading(true);
      try {
        const searchParams = new URLSearchParams(window.location.search);
        const urlToken = searchParams.get('token');
        const createData: Record<string, string> = {
          code,
          uid,
          username,
          category: config.name
        };
        if (urlToken) createData.token = urlToken;
        if (questionLeafId) createData.questionLeafId = questionLeafId;

        const createResp = await uchatApi.createCs(createData);
        if (createResp?.success === false && createResp?.code === 'C_1') {
          return;
        }
        if (createResp?.success && createResp.data) {
          const data = createResp.data;
          const newCode = data.code || code;
          if (newCode !== code) setCode(newCode);
          setQuestionStack([]);
          enterChat(
            {
              csUuid: data.uuid,
              code: newCode,
              category: config.name,
              status: data.status
            },
            { uid, username }
          );
        } else {
          message.error(createResp?.msg || 'Create session failed');
        }
      } catch (error) {
        console.error(error);
        message.error(intl.formatMessage({ id: 'chat.init_failed' }));
      } finally {
        setLoading(false);
      }
    },
    [code, uid, username, enterChat, intl]
  );

  const handleQuestionSelect = useCallback(
    (node: CategoryQuestionNode) => {
      if (isQuestionBranch(node)) {
        setQuestionStack(prev => [...prev, node.children || []]);
        return;
      }
      if (selectedCategoryConfig) {
        enterChatFromCategory(selectedCategoryConfig, node.id);
      }
    },
    [selectedCategoryConfig, enterChatFromCategory]
  );

  const handleQuestionOther = useCallback(() => {
    if (selectedCategoryConfig) {
      enterChatFromCategory(selectedCategoryConfig);
    }
  }, [selectedCategoryConfig, enterChatFromCategory]);

  const handleQuestionBack = useCallback(() => {
    setQuestionStack(prev => {
      if (prev.length <= 1) {
        setViewMode('category');
        return [];
      }
      return prev.slice(0, -1);
    });
  }, []);

  const handleStartWithoutCategory = useCallback(async () => {
    setLoading(true);
    try {
      const searchParams = new URLSearchParams(window.location.search);
      const urlToken = searchParams.get('token');
      const createData: Record<string, string> = { code, uid, username };
      if (urlToken) createData.token = urlToken;
      const createResp = await uchatApi.createCs(createData);
      if (createResp?.success && createResp.data) {
        const data = createResp.data;
        const newCode = data.code || code;
        enterChat(
          {
            csUuid: data.uuid,
            code: newCode,
            status: data.status,
            category: data.category
          },
          { uid, username }
        );
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [code, uid, username, enterChat]);

  const handleBackToCategory = useCallback(() => {
    setParams(undefined);
    setQuestionStack([]);
    setViewMode('category');
  }, []);

  /** 聊天页返回：关闭 SDK 面板，不结束会话、不回分类页 */
  const handleCloseChatPanel = useCallback(() => {
    if (window.parent !== window) {
      window.parent.postMessage({ type: 'UCHAT_CLOSE' }, '*');
      return;
    }
    window.close();
  }, []);

  const handleSessionClosed = useCallback(() => {
    setParams(prev => (prev ? { ...prev, status: 'close' } : prev));
  }, []);

  const handleBackToGame = useCallback(() => {
    if (window.parent !== window) {
      // 结束页关闭：通知 SDK 下次打开时重载 iframe，进入新会话入口
      window.parent.postMessage({ type: 'UCHAT_CLOSE', reloadOnOpen: true }, '*');
      return;
    }
    window.close();
  }, []);

  useEffect(() => {
    if (hasInitialized) return;
    const searchParams = new URLSearchParams(location.search);
    const urlCode = searchParams.get('code') || '';
    const urlUid = searchParams.get('uid') || '';
    const urlUsername = searchParams.get('username') || '';
    const urlCsUuid = searchParams.get('csUuid') || '';

    if (!urlCode) {
      setHasInitialized(true);
      setCategoryLoaded(true);
      return;
    }

    let currentUid = urlUid;
    let currentUsername = urlUsername || urlUid;
    // URL 无 uid：生成游客身份并立刻写入地址栏，避免刷新重新生成会话
    if (!currentUid && !currentUsername) {
      const guestId = `Guest-${Math.floor(Math.random() * 100000)}`;
      currentUid = guestId;
      currentUsername = guestId;
      window.history.replaceState(
        {},
        '',
        buildChatUrl({
          code: urlCode,
          uid: guestId,
          username: guestId,
          ...(urlCsUuid ? { csUuid: urlCsUuid } : {})
        })
      );
    }

    setCode(urlCode);
    setUid(currentUid);
    setUsername(currentUsername);
    setLoading(true);

    fetchSetting(urlCode)
      .then(settingRes => {
        if (settingRes?.success && settingRes.data) {
          applySetting(settingRes.data, urlCode);
        } else {
          setCategoryLoaded(true);
        }
      })
      .then(() => {
        if (urlCsUuid) {
          return uchatApi.queryCs(urlCsUuid).then(csRes => {
            if (csRes?.success && csRes.data) {
              const data = csRes.data;
              const finalUid = data.uid || currentUid;
              const finalUsername = data.username || currentUsername;
              setUid(finalUid);
              setUsername(finalUsername);
              enterChat(
                {
                  csUuid: data.uuid || urlCsUuid,
                  code: data.code || urlCode,
                  category: data.category,
                  status: data.status
                },
                { uid: finalUid, username: finalUsername }
              );
            }
          });
        }
        return uchatApi
          .createCs({
            code: urlCode,
            uid: currentUid,
            username: currentUsername
          })
          .then(createResp => {
            // 无分类配置时 createCs 会直接返回会话，须同步 uid/csUuid 到地址栏
            if (createResp?.success && createResp.data?.uuid) {
              const data = createResp.data;
              enterChat(
                {
                  csUuid: data.uuid,
                  code: data.code || urlCode,
                  category: data.category,
                  status: data.status
                },
                { uid: currentUid, username: currentUsername }
              );
            }
          });
      })
      .catch(error => console.error('init failed', error))
      .finally(() => {
        setLoading(false);
        setHasInitialized(true);
      });
  }, [location.search, hasInitialized, applySetting, enterChat, buildChatUrl]);

  useEffect(() => {
    if (params?.status === 'close' && switchChatEvaluate) {
      setViewMode('ending');
    }
  }, [params?.status, switchChatEvaluate]);

  return (
    <div
      style={{
        height: '100vh',
        background: tokens.background,
        color: tokens.text,
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      {viewMode === 'category' && !params ? (
        <UCCategoryPage
          categoryData={categoryData}
          userName={username}
          hideHeader={hideHeader}
          onCategorySelect={handleCategorySelect}
          onClose={handleCloseChatPanel}
          onStartWithoutCategory={
            categoryLoaded && !(categoryData?.configs && categoryData.configs.length > 0)
              ? handleStartWithoutCategory
              : undefined
          }
        />
      ) : null}

      {viewMode === 'question' && selectedCategoryConfig ? (
        <UCQuestionPage
          categoryConfig={selectedCategoryConfig}
          options={questionStack[questionStack.length - 1] || []}
          hideHeader={hideHeader}
          onBack={handleQuestionBack}
          onSelect={handleQuestionSelect}
          onSelectOther={handleQuestionOther}
        />
      ) : null}

      {viewMode === 'chat' && params ? (
        <UCChatView
          csUuid={params.csUuid}
          code={params.code}
          uid={uid}
          status={params.status}
          category={params.category}
          themeColors={chatThemeColors}
          isDark={isDark}
          quickQaIds={selectedCategoryConfig?.quickQa}
          hideHeader={hideHeader}
          onBack={handleCloseChatPanel}
          onChatRef={handleChatRef}
          onSessionClosed={handleSessionClosed}
        />
      ) : null}

      {viewMode === 'ending' && params ? (
        <UCEndingPage
          csUuid={params.csUuid}
          hideHeader={hideHeader}
          onBackToGame={handleBackToGame}
          onStartNewChat={handleBackToCategory}
        />
      ) : null}

      {loading ? (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.35)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2000
          }}
        >
          <Spin
            indicator={<LoadingOutlined style={{ fontSize: 48 }} spin />}
            tip={intl.formatMessage({ id: 'chat.initializing' })}
          />
        </div>
      ) : null}
    </div>
  );
};

const UChatPageV2: React.FC = () => {
  const location = useLocation();
  const [themeId, setThemeId] = useState<string | undefined>();

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const urlCode = searchParams.get('code');
    if (!urlCode) {
      setThemeId(undefined);
      return;
    }
    fetchSetting(urlCode)
      .then(res => {
        if (res?.success && res.data?.pageThemeMode) {
          setThemeId(res.data.pageThemeMode);
        } else {
          setThemeId(undefined);
        }
      })
      .catch(() => setThemeId(undefined));
  }, [location.search]);

  const theme = useMemo(() => resolveV2Theme(themeId), [themeId]);

  return (
    <PageThemeProvider theme={theme}>
      <UChatPageV2Inner />
    </PageThemeProvider>
  );
};

export default UChatPageV2;
